import { useMemo } from 'react';
import { ICompatibilityResult, IRecipient } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { ColGroupDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Button, Label } from 'react-bootstrap';
import { GridWrapper } from './styles';

interface IProps {
    data: any[],
    placeHolderRecipient: IRecipient,
    onConfirm: (recipientId: number, pairedDonorId: number) => void,
}

const PairGrid = (props: IProps) => {
    const handleDonorEvaluation = (centerId: number, recipientId: number, donorId: number) => {
        window.open('/center/' + centerId + '/recipient/' + recipientId + '/evaluate-donor/' + donorId, '_blank');
    }

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const colDefs: ColGroupDef[] = [
        {
            headerName: 'Available Pair Replacements',
            children: [
                {
                    headerName: 'Recipient',
                    children: [
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Rank</SortableHeaderComponent>,
                            cellRendererFramework: (params: ICellRendererParams) => <>{params.rowIndex + 1}</>,
                            headerName: 'Rank',
                            width: 50,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
                            headerName: 'Alias',
                            field: 'alias',
                            width: 115
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Microsite<br />Status</SortableHeaderComponent>,
                            cellRendererFramework: (params: ICellRendererParams) => {
                                return params.data.micrositeActive ? <Label bsStyle='success'>Active</Label> : <Label bsStyle='default'>Inactive</Label>
                            },
                            headerName: 'Alias',
                            field: 'micrositeActive',
                            width: 80,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
                            headerName: 'ABO',
                            field: 'bloodType',
                            width: 40,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>cPRA</SortableHeaderComponent>,
                            headerName: 'cPRA',
                            field: 'cpra4',
                            width: 50,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>UNOS Wait<br />Months</SortableHeaderComponent>,
                            headerName: 'UNOS Wait Months',
                            field: 'unosWaitMonths',
                            width: 70,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>NKR Wait<br />Months</SortableHeaderComponent>,
                            headerName: 'NKR Wait Months',
                            field: 'nkrWaitMonths',
                            width: 70,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
                            headerName: 'Age',
                            field: 'age',
                            width: 40,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet<br />MM</SortableHeaderComponent>,
                            cellRendererFramework: (params: ICellRendererParams) => {
                                let data = params.data as ICompatibilityResult;
                                return data.epletMMRisk != null && data.epletMMRisk.length > 0 ? data.epletMMRisk : 'N/A';
                            },
                            headerName: 'Eplet MM',
                            width: 70,
                        }
                    ]
                },
                {
                    headerName: 'Paired Donor',
                    children: [
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
                            headerName: 'Donor Alias',
                            field: 'pairedDonorAlias',
                            width: 70,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
                            headerName: 'Donor ABO',
                            field: 'pairedDonorAbo',
                            width: 70,
                        },
                        {
                            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
                            headerName: 'Donor Type',
                            field: 'pairedDonorType',
                            width: 70,
                        },
                    ]
                },
                {
                    headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        return (
                            <>
                                <Button bsStyle='primary' bsSize='xs' onClick={() => props.onConfirm(params.data.id, params.data.pairedDonorId)}>Request</Button>&nbsp;
                                <Button bsSize='xs' onClick={() => handleDonorEvaluation(params.data.centerId, params.data.id, params.data.currentDonorId)}>Evaluate Donor</Button>
                            </>
                        )
                    },
                    headerClass: 'list-header-last',
                    headerName: 'Actions',
                    width: 160
                }
            ]
        }
    ];

    const columnDefs = useMemo(() => colDefs, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper className='ag-theme-bootstrap' style={{ width: '100%', height: 400 }}>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={44}
                groupHeaderHeight={28}
                rowHeight={30}
                suppressCellSelection={true}
                suppressRowClickSelection={true}
                suppressContextMenu={true}
                enableCellTextSelection={true}
                alwaysShowVerticalScroll={true}
                immutableData={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onGridSizeChanged={onGridSizeChanged}
            />
        </GridWrapper>
    )
}

export default PairGrid;